import LegalLayout, { Section, List } from "@/pages/legal/LegalLayout";

export default function Disclaimer() {
    return (
        <LegalLayout
            eyebrow="Disclaimer"
            title="What HomeIQ is — and isn't."
            lede="HomeIQ is a decision-support tool, not an advisor. Every number and recommendation you see is an estimate based on the inputs you provided and the assumptions built into our calculation engines. Here is the honest read on each feature."
            lastUpdated="1 July 2026"
        >
            <Section title="Affordability estimates">
                <p>
                    The affordability figures — maximum home value, eligible loan, comfortable EMI — are produced by our FOIR-based engine using the salary, obligations, down-payment, rate and tenure you enter. They mirror the mainstream Indian lender heuristic (FOIR ≤ 55%), but each bank runs its own risk model.
                </p>
                <p className="text-[#0A1F44]/60">
                    A HomeIQ affordability read is a directional starting point. Your actual sanctioned amount depends on your credit score, employment stability, existing EMIs, co-applicant profile and the specific lender's policy. Always confirm with a formal sanction letter before making any offer.
                </p>
            </Section>

            <Section title="Loan estimates">
                <p>
                    EMI, total interest, total outflow and prepayment savings are computed using standard amortisation math at the interest rate you enter. Real loans include additional variables we do not model — processing fees, insurance riders, floating-rate resets, part-prepayment restrictions, foreclosure charges, and lender-specific promotions.
                </p>
                <p className="text-[#0A1F44]/60">
                    Use HomeIQ to compare structural scenarios (tenure vs rate vs prepayment). Rely on the lender's official amortisation schedule for the numbers you sign against.
                </p>
            </Section>

            <Section title="Market intelligence">
                <p>
                    HomeIQ's market view is built from a curated dataset of 24 verified Bengaluru micro-markets. For each, we track a price-per-square-foot band, a five-year CAGR, metro connectivity, and lifestyle context. The best-market recommendation ranks localities that fit your budget by growth score.
                </p>
                <p className="text-[#0A1F44]/60">
                    Real-estate markets shift with policy, infrastructure and macro events. Our numbers reflect the most recent data point we've verified — they are not a live index and may lag actual on-ground pricing. Always cross-check with recent registered transactions before committing.
                </p>
            </Section>

            <Section title="Investment projections">
                <p>
                    The buy-vs-rent comparison, break-even year, and wealth-at-horizon numbers are model-driven projections. They assume a property CAGR (drawn from the best-fit market in our dataset), an equity CAGR of 12% for the rent-invest alternative, a 5% rent escalation, and standard maintenance costs.
                </p>
                <p className="text-[#0A1F44]/60">
                    These assumptions are reasonable long-term averages for Indian salaried buyers. They will not be true in every year, and are especially sensitive to interest-rate cycles and location-specific growth surprises.
                </p>
            </Section>

            <Section title="Vastu analysis">
                <p>
                    The Vastu Analyzer uses Anthropic's Claude model to read the directions of key rooms from your uploaded floor plan, then a rule-based engine we maintain scores the plan across six dimensions (main entrance, kitchen, master bedroom, toilets, living & balcony, pooja / energy zone).
                </p>
                <p className="text-[#0A1F44]/60">
                    Two important limits: <strong>(i)</strong> the AI can misread rooms, especially in low-quality scans or floor plans with unusual layouts — please review the "Confirm" step carefully. <strong>(ii)</strong> Vastu is a traditional Indian discipline with multiple schools of interpretation. Our read is <em>one</em> systematic scoring approach; it is not consulted from a lineage practitioner. Vastu is intentionally excluded from your HomeIQ Composite Score — it is advisory only.
                </p>
            </Section>

            <Section title="The HomeIQ Report PDF">
                <p>
                    Every report is a snapshot of your inputs and the resulting calculations at the moment you generated it. If you change your rent, salary or other inputs later, previously-generated reports will not update — they are permanent records.
                </p>
                <p className="text-[#0A1F44]/60">
                    Reports are stored against a unique Report ID and can be downloaded again from the same URL. If you'd like a report deleted, contact us with the ID.
                </p>
            </Section>

            <Section title="Landing-page numbers">
                <p>
                    On the landing page, the home-value and EMI figures are computed using a rent-inferred reference profile (roughly, salary ≈ rent × 5). This lets the slider feel responsive without asking for every input upfront. The numbers are labelled as estimates for a typical buyer profile — they are <em>not</em> personalised until you complete the Affordability Assessment.
                </p>
            </Section>

            <Section title="Before you sign anything">
                <p>Final property decisions should always involve the appropriate professionals:</p>
                <List
                    items={[
                        <><strong>Lender or mortgage broker</strong> — for a formal sanction letter with the actual EMI, rate and fees.</>,
                        <><strong>Property lawyer</strong> — for the sale agreement, title verification, RERA status and society dues.</>,
                        <><strong>Chartered accountant</strong> — for tax implications, capital-gains planning, and joint-ownership structure.</>,
                        <><strong>Qualified Vastu practitioner</strong> — if Vastu is decisive for you.</>,
                        <><strong>Registered valuer or architect</strong> — for construction quality, structural fitness, and re-sale realism.</>,
                    ]}
                />
                <p>
                    HomeIQ is designed to help you walk into each of these conversations better prepared — not to replace them.
                </p>
            </Section>

            <Section title="Questions">
                <p>
                    If a number in HomeIQ doesn't match what you're seeing elsewhere and you'd like us to explain the methodology, message us on WhatsApp at <a href="https://wa.me/917406054477" target="_blank" rel="noopener noreferrer" className="text-[#1E6BFF] font-semibold hover:underline">+91 74060 54477</a>.
                </p>
            </Section>
        </LegalLayout>
    );
}
